import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./ModelViewer.scss";
import Loader from "components/Loader";
import MixPanel from "appAnalytics/mixPanel";
import { EVENT_CATEGORIES, EVENT_ACTIONS } from "appAnalytics/eventCategories";
import { isIos } from "utils";
import { isJockey1842Inline, isJockey1820Inline } from "utils/jockeyInlineCamera";
import { applyWheatishTone, loadStoredTone } from "utils/jockeySkinTones";

// Jockey inline routes force a white IBL — match the override in
// ModelViewerContainer.getDefaultEnvironment so the imperative
// useEffect below doesn't write the warm pillars HDRI back in.
const JOCKEY_INLINE_ENV = "/environment/HDRI_White_02A.hdr";
// 1820 uses the POC-picker stage setup: pillars_1k.hdr as both env + skybox,
// exposure 1.0, neutral tone-mapping.
const JOCKEY_1820_HDR = "/environment/pillars_1k.hdr";


const ModelViewer = ({
  ar,
  arModes,
  cameraControls,
  toneMapping,
  poster,
  shadowIntensity,
  environmentImage,
  autoRotate,
  cameraPosition,
  config,
  disablePan,
  loading,
  setLoading,
  showHotspots,
  skyboxImage,
  exposure,
  resetCamera,
  onActiveHotspotChange,
  externalActiveHotspotId,
  onHotspotMark,
  selectedSkybox,
  scale,
}) => {
  const { src, hotspots = [], additionalAssets } = config;
  const modelViewerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [activeHotspot, setActiveHotspot] = useState(
    externalActiveHotspotId || hotspots?.[0]?.slot || ""
  );
  const [prevShowHotspots, setPrevShowHotspots] = useState("");
  const [prevCameraPosition, setPrevCameraPosition] = useState(cameraPosition);
  const [userInteracted, setUserInteracted] = useState(false);
  const mouseDownPos = useRef({ x: 0, y: 0 });
  const autoMoveInterval = useRef(null);

  const annotationClicked = (event, hotspot) => {
    const modelViewer = modelViewerRef.current;
    if (modelViewer) {
      if (event) {
        MixPanel.buttonClicked(EVENT_CATEGORIES.PERSONAL_AVATAR, EVENT_ACTIONS.ANNOTATION_CLICKED, { annotation: hotspot.annotation });
        const positionAndNormal = modelViewer.positionAndNormalFromPoint(
          event.clientX,
          event.clientY
        );
        const orbit = modelViewer.getCameraOrbit();
        if (positionAndNormal)
          console.log(
            "hotspot",
            hotspot.annotation,
            JSON.stringify({
              normal: positionAndNormal.normal.toString(),
              modelOrbit: orbit.toString(),
              modelPosition: positionAndNormal.position.toString(),
            })
          );
      }
      modelViewer.cameraTarget = cameraPosition.cameraTarget;
      modelViewer.fieldOfView = cameraPosition.fieldOfView;
      modelViewer.cameraOrbit = hotspot.orbit;
      setActiveHotspot(hotspot.slot);
      onActiveHotspotChange?.(hotspot.slot);
    } else {
      if (autoMoveInterval.current) {
        clearInterval(autoMoveInterval.current);
        autoMoveInterval.current = null;
      }
    }
  };

  const handleClick = async (event) => {
    const modelViewer = modelViewerRef.current;
    if (loading || !modelViewer.model || event.button !== 0) return;
    const dx = event.clientX - mouseDownPos.current.x;
    const dy = event.clientY - mouseDownPos.current.y;
    const dragDistance = Math.sqrt(dx * dx + dy * dy);
    if (dragDistance > 5) return; // Ignore if it's a drag, not a click

    setUserInteracted(true);
    if (autoMoveInterval.current) {
      clearInterval(autoMoveInterval.current);
      autoMoveInterval.current = null;
    }

    const positionAndNormal = modelViewer.positionAndNormalFromPoint(
      event.clientX,
      event.clientY
    );

    if (positionAndNormal && onHotspotMark) {
      const orbit = modelViewer.getCameraOrbit();
      onHotspotMark({
        position: positionAndNormal.position,
        normal: positionAndNormal.normal,
        cameraOrbit: orbit,
      });
    }

    const hit = await modelViewer.materialFromPointExtended?.(
      event.clientX,
      event.clientY
    );
    if (!hit?.material) {
      resetCamera();
    }
  };

  const onMouseDown = (event) => {
    if (event.button === 0) {
      mouseDownPos.current = { x: event.clientX, y: event.clientY };
    }
  };

  const startAutoMove = () => {
    // Disabled: hotspots are commented out in the JSX, but the auto-cycle
    // (every 3s the camera snaps to the next hotspot) was still running.
    return;
    // eslint-disable-next-line no-unreachable
    if (!userInteracted && hotspots.length > 1) {
      let currentIndex = hotspots.findIndex((h) => h.slot === activeHotspot);
      autoMoveInterval.current = setInterval(() => {
        currentIndex = (currentIndex + 1) % hotspots.length;
        const nextHotspot = hotspots[currentIndex];
        annotationClicked(null, nextHotspot);
      }, 3000);
    }
  };

  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    const onBeforeRender = () => {
      if (modelViewerRef.current) {
        const modelViewer = modelViewerRef.current;
        const scene =
          modelViewer[
            Object.getOwnPropertySymbols(modelViewer).find(
              (e) => e.description === "scene"
            )
          ];
        // Apply scale to a common parent so all assets scale together
        const groupToScaleInit =
          (scene && (scene.pivot || scene.target || scene.model)) || null;
        if (groupToScaleInit && scale && typeof scale.x === "number") {
          groupToScaleInit.scale.set(scale.x || 1, scale.y || 1, scale.z || 1);
        }
        // Skip the alpha-test hair clone trick for any Jockey POC bra
        // (enamor 210-220 — MI16, 1816, MI10, …). The new picker-based
        // avatars don't need the dual-pass hair pass, and re-bindings
        // of this useEffect were stacking a new clone on every camera
        // interaction. Matches BabylonViewer.js#isJockeyPocAvatar.
        const modelSrc = config?.src || "";
        const isJockeyPoc =
          /female_avatar_enamor21[0-9]/.test(modelSrc) ||
          /\b21[0-9]A_0?\b/.test(modelSrc) ||
          (typeof window !== "undefined" &&
            /\/jockeydemo\/(216|1816|1820|1840|1841|1844|MI10|JW11)\b/i.test(
              window.location.pathname
            ));

        const hairMesh =
          !isJockeyPoc &&
          (scene.getObjectByName("Shoulder_Hair_OBJ") ||
            scene.getObjectByName("dopplr_hair") ||
            scene.getObjectByName("dopplr_hair2"));
        // Idempotency guard — don't re-clone if a previous load already
        // created the clone (otherwise repeated load fires stack copies
        // each at 0.88× scale of the last one).
        const alreadyCloned =
          hairMesh && scene.getObjectByName(`${hairMesh.name}_clone`);
        if (hairMesh && !alreadyCloned) {
          const cloneHairMesh = hairMesh.clone();
          cloneHairMesh.name = `${hairMesh.name}_clone`;

          // Clone material
          const hairMaterial = hairMesh.material.clone();

          // Simulate MATERIAL_ALPHATEST
          hairMaterial.alphaTest = 0.5; // Typical threshold
          hairMaterial.transparent = false; // ALPHATEST: not alpha blended
          hairMaterial.depthWrite = true;

          // No specular and metallic (match Babylon settings)
          hairMaterial.roughness = 1; // Maximum roughness
          hairMaterial.metalness = 0; // No metallic

          // Set original hairMesh to same settings too
          hairMesh.material.roughness = 1;
          hairMesh.material.metalness = 0;
          cloneHairMesh.material = hairMaterial;
          cloneHairMesh.scale.y = 0.88 * cloneHairMesh.scale.y;
          scene.model.add(cloneHairMesh);
        }
      }
      setTimeout(() => {
        // setProgress("");
        setLoading(false);
      }, 10);
    };

    const handleLoading = () => {
      resetCamera();
      onBeforeRender();
    };
    const handleProgress = (event) => {
      setLoading(true);
      const { totalProgress } = event.detail;
      setProgress(Math.round(Number(totalProgress) * 100));
    };
    if (modelViewer && loading && progress === 100) {
      setLoading(false);
    }
    modelViewer.addEventListener("progress", handleProgress);
    modelViewer.addEventListener("load", handleLoading);
    return () => {
      modelViewer.removeEventListener("progress", handleProgress);
      modelViewer.removeEventListener("load", handleLoading);
      if (isIos() && autoMoveInterval.current) {
        clearInterval(autoMoveInterval.current);
        autoMoveInterval.current = null;
      }

      // Aggressive memory cleanup was previously here for iOS — disabled
      // because it disposed geometries/materials off the SHARED Three.js
      // singleton, leaving subsequent model-viewer instances unable to
      // render (blank screen on shape/placement/size change in iOS / DevTools
      // iPhone emulation). model-viewer manages its own cleanup via
      // disconnectedCallback when the element is removed via React unmount.
    };
  }, [progress, setLoading, setProgress, loading, resetCamera]);

  // Update scale dynamically when prop changes and model is available
  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv || !scale) return;
    const scene =
      mv[
        Object.getOwnPropertySymbols(mv).find((e) => e.description === "scene")
      ];
    const groupToScale =
      (scene && (scene.pivot || scene.target || scene.model)) || null;
    if (groupToScale && typeof scale.x === "number") {
      groupToScale.scale.set(scale.x || 1, scale.y || 1, scale.z || 1);
    }
  }, [scale]);

  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv || !selectedSkybox?.environment) return;
    // Jockey inline overrides take precedence — modelViewerList[0] is
    // pillars_1k.hdr which has a warm sunset horizon that tints the
    // lit avatar warm. Force white IBL + a brighter baseline exposure
    // so the avatar reads cleanly on the white stage.
    if (isJockey1820Inline()) {
      // POC-picker parity: pillars HDR as env + skybox, exposure 1.6,
      // neutral tone-mapping. tone-mapping is set on the element attribute
      // below; mv.toneMapping mirror just keeps the JS state in sync.
      mv.environmentImage = JOCKEY_1820_HDR;
      mv.skyboxImage = JOCKEY_1820_HDR;
      mv.exposure = 1.6;
      mv.toneMapping = "neutral";
    } else if (isJockey1842Inline()) {
      mv.environmentImage = JOCKEY_INLINE_ENV;
      mv.skyboxImage = "";
      mv.exposure = 3.0;
    } else {
      mv.environmentImage = selectedSkybox.environment ?? "";
      mv.skyboxImage = selectedSkybox.skybox ?? "";
    }
    mv.skyboxHeight = "1.5m";
    if (selectedSkybox.exposure !== undefined) mv.exposure = selectedSkybox.exposure;
  }, [selectedSkybox]);

  useEffect(() => {
    if (externalActiveHotspotId !== undefined) {
      setActiveHotspot(externalActiveHotspotId);
    }
  }, [externalActiveHotspotId]);

  // Local-only lighting toggle for the 1820 inline demo: a sun-icon button
  // in the parent (Jockey1820Inline) postMessages SET_LIGHTING_MODE with
  // mode "pillars" or "legacy". On "legacy" we drop the HDR env+skybox so
  // model-viewer falls back to its built-in 3-point lighting. On "pillars"
  // we re-apply the POC-picker stage that the main useEffect normally sets.
  useEffect(() => {
    if (!isJockey1820Inline()) return;
    const handler = (event) => {
      if (event?.data?.type !== "SET_LIGHTING_MODE") return;
      const mv = modelViewerRef.current;
      if (!mv) return;
      if (event.data.mode === "legacy") {
        mv.environmentImage = "";
        mv.skyboxImage = "";
        mv.exposure = 1.0;
        mv.toneMapping = "commerce";
      } else {
        mv.environmentImage = JOCKEY_1820_HDR;
        mv.skyboxImage = JOCKEY_1820_HDR;
        mv.exposure = 1.6;
        mv.toneMapping = "neutral";
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Re-apply the picker-stage camera (cameraOrbit + cameraTarget + FoV)
  // after model-viewer's `load` event. On a fresh page load — especially
  // on mobile where the GLB download takes longer — model-viewer's
  // built-in framing recompute kicks in once the model lands and
  // overrides the camera-orbit we set in the render body. A page refresh
  // hides the bug because the GLB is already cached, so the set wins the
  // race. Keeping the current cameraPosition in a ref so the load
  // handler always reads the latest value (otherwise the effect closes
  // over the value at mount and re-applies stale settings).
  const cameraPositionRef = useRef(cameraPosition);
  useEffect(() => { cameraPositionRef.current = cameraPosition; }, [cameraPosition]);

  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv) return;
    const onLoad = () => {
      requestAnimationFrame(() => {
        const cp = cameraPositionRef.current;
        if (!cp || !modelViewerRef.current) return;
        if (cp.cameraTarget) modelViewerRef.current.cameraTarget = cp.cameraTarget;
        if (cp.cameraOrbit)  modelViewerRef.current.cameraOrbit  = cp.cameraOrbit;
        if (cp.fieldOfView)  modelViewerRef.current.fieldOfView  = cp.fieldOfView;
      });
    };
    mv.addEventListener("load", onLoad);
    return () => mv.removeEventListener("load", onLoad);
  }, []);

  // Skin-tone tinting for Jockey inline (1820/1841/MI16). Two paths:
  //   1. The model-viewer fires `load` whenever a new GLB swaps in
  //      (shape change, product switch). We re-apply the stored tone so
  //      the tint survives. localStorage is the source of truth.
  //   2. ModelViewerContainer postMessages SET_SKIN_TONE when the user
  //      picks a swatch — we apply immediately + the container saved it.
  useEffect(() => {
    if (!isJockey1820Inline()) return;
    const mv = modelViewerRef.current;
    if (!mv) return;
    const reapplyFromStorage = () => {
      // Defer one frame — model.materials sometimes isn't fully populated
      // on the exact tick of the load event.
      requestAnimationFrame(() => applyWheatishTone(mv, loadStoredTone()));
    };
    mv.addEventListener("load", reapplyFromStorage);
    // Initial apply in case the model already loaded before this effect.
    reapplyFromStorage();
    const msgHandler = (event) => {
      if (event?.data?.type !== "SET_SKIN_TONE") return;
      const idx = Number(event.data.toneIdx);
      if (!Number.isFinite(idx)) return;
      applyWheatishTone(mv, idx);
    };
    window.addEventListener("message", msgHandler);
    return () => {
      mv.removeEventListener("load", reapplyFromStorage);
      window.removeEventListener("message", msgHandler);
    };
  }, []);

  const animateFeatures = () => {
    // Disabled: hotspots are commented out, so suppress both the initial
    // camera snap-to-active-hotspot and the 3s cycle. Only keep the cleanup
    // branch in case a stale interval was set elsewhere.
    if (autoMoveInterval.current) {
      clearInterval(autoMoveInterval.current);
      autoMoveInterval.current = null;
    }
    return;
    // eslint-disable-next-line no-unreachable
    if (showHotspots && hotspots.length > 0) {
      modelViewerRef.current.interactionPrompt = "none";
      setTimeout(() => {
        annotationClicked(
          null,
          hotspots.find((h) => h.slot === activeHotspot)
        );
        startAutoMove();
      }, 500);
    } else {
      if (autoMoveInterval.current) {
        clearInterval(autoMoveInterval.current);
        autoMoveInterval.current = null;
      }
      setUserInteracted(false);
    }
  };

  if (prevCameraPosition !== cameraPosition) {
    setPrevCameraPosition(cameraPosition);
    modelViewerRef.current.cameraTarget = cameraPosition.cameraTarget;
    modelViewerRef.current.fieldOfView = cameraPosition.fieldOfView;
    modelViewerRef.current.cameraOrbit = cameraPosition.cameraOrbit;
    modelViewerRef.current.interactionPrompt =
      showHotspots && hotspots.length > 0 ? "none" : "auto";
  }

  if (
    modelViewerRef.current &&
    prevShowHotspots !== showHotspots &&
    activeHotspot
  ) {
    setPrevShowHotspots(showHotspots);
    animateFeatures();
  }


  return (
    <>
      <model-viewer
        id="model-viewer"
        ref={modelViewerRef}
        interpolation-decay="100"
        style={{ display: "block", width: "100%", height: "100%" }}
        src={src}
        {...(additionalAssets ? { "additional-models": additionalAssets } : {})}
        {...(ar ? { ar: "" } : {})}
        {...(arModes ? { "ar-modes": arModes } : {})}
        {...(cameraControls ? { "camera-controls": "" } : {})}
        tone-mapping={toneMapping}
        poster={poster}
        exposure={exposure}
        shadow-intensity={shadowIntensity}
        skybox-height="1.5m"
        {...(skyboxImage ? { "skybox-image": skyboxImage } : {})}
        {...(environmentImage ? { "environment-image": environmentImage } : {})}
        {...(autoRotate ? { "auto-rotate": "" } : {})}
        {...(disablePan ? { "disable-pan": "" } : {})}
        field-of-view={cameraPosition.fieldOfView}
        camera-target={cameraPosition.cameraTarget}
        camera-orbit={cameraPosition.cameraOrbit}
        min-camera-orbit={cameraPosition.minCameraOrbit}
        max-camera-orbit={cameraPosition.maxCameraOrbit}
        onClick={handleClick}
        onMouseDown={onMouseDown}
      >
        {/* {showHotspots &&
          hotspots.map((hotspot, index) => (
            <button
              key={index}
              className={`Hotspot ${
                activeHotspot === hotspot.slot ? "active" : ""
              }`}
              slot={hotspot.slot}
              data-position={hotspot.position}
              data-normal={hotspot.normal}
              data-visibility-attribute="visible"
              onClick={(e) => showHotspots && annotationClicked(e, hotspot)}
            >
              {hotspot.slot === activeHotspot && (
                <div
                  className={`HotspotAnnotation ${
                    hotspot.orbit &&
                    parseFloat(hotspot.orbit.split(" ")[0]) > 1.2
                      ? "right"
                      : ""
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {hotspot.annotation}
                </div>
              )}
            </button>
          ))} */}
        <div className="progress-bar hide" slot="progress-bar">
          <div className="update-bar"></div>
        </div>
        {/* <button slot="ar-button" id="ar-button">
        {arButtonText}
      </button> */}
      </model-viewer>
      {loading && (
        <Loader className="ModelViewer__Loader" progress={progress} />
      )}
    </>
  );
};

ModelViewer.propTypes = {
  config: PropTypes.shape({
    src: PropTypes.string,
    hotspots: PropTypes.arrayOf(
      PropTypes.shape({
        slot: PropTypes.string,
        position: PropTypes.string,
        normal: PropTypes.string,
        annotation: PropTypes.string,
        orbit: PropTypes.string,
      })
    ),
  }),
  selectedProduct: PropTypes.any,
  ar: PropTypes.bool,
  arModes: PropTypes.string,
  cameraControls: PropTypes.bool,
  toneMapping: PropTypes.string,
  poster: PropTypes.string,
  shadowIntensity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  environmentImage: PropTypes.string,
  cameraOrbit: PropTypes.string,
  fieldOfView: PropTypes.string,
  autoRotate: PropTypes.bool,
  cameraTarget: PropTypes.string,
  minCameraOrbit: PropTypes.string,
  maxCameraOrbit: PropTypes.string,
  arButtonText: PropTypes.string,
  disablePan: PropTypes.bool,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  exposure: PropTypes.string,
  resetCamera: PropTypes.func,
  onActiveHotspotChange: PropTypes.func,
  activeHotspot: PropTypes.string,
  onHotspotMark: PropTypes.func,
  scale: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    z: PropTypes.number,
  }),
};

ModelViewer.defaultProps = {
  src: "",
  ar: true,
  arModes: "",
  cameraControls: true,
  toneMapping: "aces",
  poster: "",
  shadowIntensity: "1",
  // skyboxImage: "/environment/pillars_1k.hdr",
  // environmentImage: "/environment/pillars_1k.hdr",
  // cameraOrbit: "-30.04deg 90.41deg 2.8m",
  fieldOfView: "13deg",
  autoRotate: false,
  // cameraTarget: "0.01m 1.25m 0m",
  maxCameraOrbit: "auto 120deg 2.8m",
  hotspots: [],
  arButtonText: "View in your space",
  disablePan: false,
  loading: true,
  setLoading: () => {},
  resetCamera: () => {},
  exposure: 1,
  onActiveHotspotChange: null,
  activeHotspot: undefined,
  onHotspotMark: null,
  scale: { x: 1, y: 1, z: 1 },
};

export default ModelViewer;
